import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { getDrugDiseaseDetails } from "./actions";
import MarkdownPreview from "@uiw/react-markdown-preview";
import { ChevronDown } from "lucide-react";

const DrugDiseaseDetails = () => {
  const { id, tabId } = useParams();
  const [sections, setSections] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      const isDisease = tabId !== "drugs";
      const data = await getDrugDiseaseDetails(Number(id), isDisease);
      setSections(data);
      setLoading(false);
    };
    fetchDetails();
  }, [id, tabId]);

  const toggleAccordion = (idx: number) => {
    setExpandedIdx((prev) => {
      const newIdx = prev === idx ? null : idx;
      setTimeout(() => {
        if (newIdx !== null && sectionRefs.current[newIdx]) {
          sectionRefs.current[newIdx]?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100); // Wait for expand animation
      return newIdx;
    });
  };

  if (loading) {
    return (
      <div className="w-full py-4">
        {[...Array(5)].map((_, i) => (
          <div
            className="mb-4 bg-white dark:bg-darkCardBackground rounded-2xl border border-grid dark:border-gray-700 overflow-hidden p-6 w-full animate-pulse flex items-center"
            style={{ minHeight: 56 }}
            key={i}
          >
            {/* Title skeleton (one line, like collapsed accordion) */}
            <div className="h-5 w-1/3 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="w-full py-4">
      {sections.map((section, idx) => (
        <div
          key={section.section_name || idx}
          ref={(el) => (sectionRefs.current[idx] = el)}
          className="mb-4 bg-white dark:bg-darkCardBackground rounded-2xl border border-grid dark:border-gray-700 overflow-hidden transition-all duration-300 hover:border-primary/60 dark:hover:border-primary/60"
        >
          <button
            className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors"
            onClick={() => toggleAccordion(idx)}
          >
            <span className="text-slate-800 dark:text-white font-medium text-md">
              {section.section_name}
            </span>
            <ChevronDown
              className={`w-5 h-5 text-primary dark:text-primary transition-transform duration-300 ease-in-out ${
                expandedIdx === idx ? "transform rotate-180" : ""
              }`}
            />
          </button>
          <div
            className={`transition-all duration-300 ease-in-out ${
              expandedIdx === idx ? "opacity-100" : "opacity-0 hidden"
            }`}
          >
            <div className="px-6 py-4 bg-grid/40 dark:bg-[#191919] border-t border-grid dark:border-gray-700 text-sm">
              <MarkdownPreview
                source={section.value}
                className="markdown-body dark:!text-white"
                style={{
                  backgroundColor: "transparent",
                  color: "inherit",
                  fontSize: 14,
                }}
                data-color-mode="auto"
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default DrugDiseaseDetails;
